import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Card,
  CardContent,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Grid,
  Typography,
  Box,
  CardHeader,
  Checkbox,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import { ProgressBar, Accordion, Nav } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";

import { useNotification } from "config/Notification";

import {
  fetchAllStandards as fetchStandardsService,
  saveNewStandard,
  saveNewControlByStandard,
  saveNewQuestionByStandardControl,
  fetchControlsByStandardId,
  fetchSubControlsByStandardControl,
  updateControlsweightages,
  fetchMasterSchemaByStandardId,
} from "services/modules/cap/standardservice";
import Breadcrumbs from "config/Breadcrumb";

// US-CAP-103 / US-CAP-206: QuestionRenderer for live preview
import QuestionRenderer, {
  QUESTION_TYPE_LABELS,
} from "../shared/QuestionRenderer";

// US-CAP-103: Valid question types
const VALID_QUESTION_TYPES = [
  "five_option",
  "yes_no",
  "checkbox",
  "radio",
  "text_box",
];

// US-CAP-103: Default answer_config per type
const getDefaultAnswerConfig = (type) => {
  switch (type) {
    case "five_option":
      return null;
    case "yes_no":
      return { labels: { yes: "Yes", no: "No", na: "N/A" } };
    case "checkbox":
      return { options: ["Option 1", "Option 2"], minSelect: 1, maxSelect: 2 };
    case "radio":
      return { options: ["Option 1", "Option 2", "Option 3"] };
    case "text_box":
      return { placeholder: "", reviewerInstructions: "", maxLength: 2000 };
    default:
      return null;
  }
};

const ConfigureNewStandard = () => {
  const standardTypes = [
    { id: "standard-based", name: "Standard Based" },
    { id: "domain-based", name: "Domain Based" },
  ];

  const [standardType, setStandardType] = useState(standardTypes[0].id);

  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedStandard, setSelectedStandard] = useState([]);
  const [controls, setControls] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);

  // React router
  const navigate = useNavigate();

  const { openNotification } = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [collapsedControls, setCollapsedControls] = useState([]);

  const toggleCollapse = (index) => {
    setCollapsedControls((prev) => {
      const isCollapsed = prev.includes(index);
      if (isCollapsed) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  // Fetch standards data based on the selected standard type
  const fetchStandardsData = async () => {
    try {
      const response = await fetchStandardsService(
        standardType === "standard-based" ? "Main Standard" : "Domain Standard",
      );
      setSelectedStandards(response.data);
    } catch (error) {
      openNotification("Error while fetching data", "error");
    }
  };

  const fetchCompanyStandardData = async () => {
    try {
      const params = {
        StandardID: formikStep2.values.standardName,
      };
      const response = await fetchMasterSchemaByStandardId(params);
      setSelectedStandard(response.data);
    } catch (error) {
      openNotification("Error while fetching data", "error");
    }
  };

  useEffect(() => {
    if (activeStep === 4) {
      fetchCompanyStandardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // Fetch controls based on the selected standard
  const fetchControlsDataByStandard = async (standard) => {
    try {
      const response = await fetchControlsByStandardId({ standard });
      setControls(response.data || []);
    } catch (error) {
      openNotification("Error while fetching controls", "error");
    }
  };

  // Fetch sub-controls based on the selected control
  const fetchSubControlsDataByControl = async (standardId, controlId) => {
    try {
      const response = await fetchSubControlsByStandardControl({
        standardId,
        controlId,
      });
      setQuestions(response.data || []);
    } catch (error) {
      openNotification("Error while fetching sub-controls", "error");
    }
  };

  // This method handles to change the pages
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // This method handles to change the rows per page based on user selection
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Validation schemas for each step
  const validationSchemaStep1 = Yup.object({
    name: Yup.string().required("Required"),
  });

  // Formik setup for step 1
  const formikStep1 = useFormik({
    initialValues: {
      name: "",
      description: "",
      acronym: "",
      group: "",
      status: true,
    },
    validationSchema: validationSchemaStep1,
    onSubmit: async (values) => {
      try {
        // Check if there's an ID to determine if it's an edit
        let body = {
          name: values.name,
          description: values.description,
          group:
            standardType === "standard-based"
              ? "Main Standard"
              : "Domain Standard",
          status: values.status,
        };

        const response = await saveNewStandard({ body });
        if (response.status === 201) {
          fetchStandardsData();
          openNotification("Standard saved successfully", "success");
          formikStep1.resetForm();
          setActiveStep((prev) => prev + 1);
          setProgress(progress + 25);
          formikStep2.setFieldValue("standard", response.data.standard.id);
          formikStep3.setFieldValue("standard", response.data.standard.id);
        }
      } catch (error) {
        openNotification("Error while saving standard", "error");
      }
    },
  });

  const validationSchemaStep2 = Yup.object({
    standard: Yup.string().required("Required"),
    controlId: Yup.string().required("Required"),
    slno: Yup.string().required("Required"),
    controlName: Yup.string().required("Required"),
  });

  // Formik setup for step 2
  const formikStep2 = useFormik({
    initialValues: {
      standardName: "",
      standard: "",
      controlId: "",
      slno: "",
      controlName: "",
      controlDescription: "",
      status: true,
    },
    validationSchema: validationSchemaStep2,
    //TE for Duplicate control ID start
    onSubmit: async (values) => {
      const body = {
        Standard: values.standardName,
        ControlID: values.controlId,
        slno: values.slno,
        Title: values.controlName,
        Description: values.controlDescription,
        status: values.status,
      };

      const response = await saveNewControlByStandard({
        standard: values.standard,
        body,
      });

      // SUCCESS
      if (response?.status === 201) {
        openNotification("Control saved successfully", "success");

        fetchControlsDataByStandard(values.standard);

        let standardName = values.standardName;
        let standard = values.standard;

        formikStep2.resetForm();
        formikStep2.setFieldValue("standardName", standardName);
        formikStep2.setFieldValue("standard", standard);
      }

      // DUPLICATE
      else if (response?.status === 400) {
        openNotification(
          response?.data?.message || "The control already exists",
          "error",
        );
      }

      // OTHER
      else {
        openNotification(
          response?.data?.message || "Error while saving control",
          "error",
        );
      }
    },
    //TE for Duplicate control ID end
  });

  const validationSchemaStep3 = Yup.object({
    standard: Yup.string().required("Required"),
    control: Yup.string().required("Required"),
  });

  // Formik setup for step 3
  const formikStep3 = useFormik({
    initialValues: {
      standardName: "",
      standard: "",
      control: "",
      subControlId: "",
      subControlName: "",
      slno: "",
      questionId: "",
      questionName: "",
      questionDescription: "",
      status: true,
      // US-CAP-103: question type configuration
      question_type: "five_option",
      answer_config: null,
      // US-CAP-206: informational flag
      informational: false,
    },
    validationSchema: validationSchemaStep3,
    onSubmit: async (values) => {
      try {
        const body = {
          Standard: values.standardName,
          ControlID: values.control,
          SubControlID: values.subControlId,
          slno: values.slno,
          SubControlName: values.subControlName,
          Question_ID: values.questionId,
          Question: values.questionName,
          Description: values.questionDescription,
          status: values.status,
          // US-CAP-103: question type
          question_type: values.question_type,
          answer_config: values.answer_config,
          // US-CAP-206: informational flag
          informational: values.informational,
        };
        let standard = values.standard;
        let standardName = values.standardName;
        let control = values.control;
        // Save new question logic
        const response = await saveNewQuestionByStandardControl({
          standard,
          body,
        });
        if (response.status === 201) {
          openNotification("Question saved successfully", "success");
        }

        fetchSubControlsDataByControl(
          formikStep3.values.standard,
          formikStep3.values.control,
        );
        formikStep3.resetForm();
        formikStep3.setFieldValue("standardName", standardName);
        formikStep3.setFieldValue("standard", standard);
        formikStep3.setFieldValue("control", control);
        formikStep3.setFieldValue("question_type", "five_option");
        formikStep3.setFieldValue("answer_config", null);
        formikStep3.setFieldValue("informational", false);
      } catch (error) {
        openNotification("Error while saving question", "error");
      }
    },
  });

  const validateTotalWeightage = (weightages) => {
    const total = weightages.reduce(
      (acc, curr) => acc + parseFloat(curr.weight),
      0,
    );
    // Round the total to avoid floating-point precision issues
    const roundedTotal = Math.round(total * 100) / 100;

    if (roundedTotal < 100) {
      return (
        "Total weightage is " +
        roundedTotal +
        ",but must be exactly 100, should not be less than 100."
      );
    } else if (roundedTotal > 100) {
      return (
        "Total weightage is " +
        roundedTotal +
        ",but  must be exactly 100, should not be greater than 100."
      );
    } else {
      return null;
    }
  };

  const validationSchemaStep4 = Yup.object({
    standard: Yup.string().required("Required"),
    weightages: Yup.array().of(
      Yup.object().shape({
        id: Yup.string().required("Required"),
        weight: Yup.number()
          .required("Required")
          .min(0, "Min value is 0")
          .max(100, "Max value is 100")
          .test(
            "is-decimal",
            "Weight must be a valid decimal (e.g., 0.2, 1.2, 1.55)",
            (value) => /^\d+(\.\d{1,2})?$/.test(value),
          ),
      }),
    ),
  });

  // Formwik setup for step 4
  const formikStep4 = useFormik({
    initialValues: {
      standard: formikStep2.values.standard,
      weightages: controls.map((contrl) => ({
        id: contrl.id,
        controlID: contrl.ControlID,
        control: contrl.Title,
        weight: contrl.Weightage || 0,
      })),
    },
    validationSchema: validationSchemaStep4,
    onSubmit: async (values) => {
      const error = validateTotalWeightage(values.weightages);
      if (error) {
        openNotification(error, "error");
        return;
      }
      try {
        let bodylist = [];
        // eslint-disable-next-line array-callback-return
        values.weightages.map((e) => {
          let obj = {
            id: e.id,
            ControlID: e.controlID,
            Weightage: e.weight,
          };
          bodylist.push(obj);
        });
        let standarId = values.standard;

        const response = await updateControlsweightages(standarId, {
          body: bodylist,
        });
        if (response.status === 200) {
          openNotification(
            "Controls weightages updated successfully",
            "success",
          );
          fetchControlsDataByStandard(formikStep2.values.standard);
        }
      } catch (error) {
        openNotification("Error while updating Controls weightages", "error");
      }
    },
    enableReinitialize: true, // Allow reinitialization when Controls weightages change
  });

  useEffect(() => {
    if (activeStep === 0) {
      fetchStandardsData();
      formikStep1.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standardType]);

  useEffect(() => {
    // Find the selected standard name
    if (formikStep2.values.standard && selectedStandards) {
      const selectedStandard = selectedStandards.find(
        (standard) => standard.id === formikStep2.values.standard,
      );
      if (selectedStandard) {
        formikStep2.setFieldValue("standardName", selectedStandard.stdid);
        formikStep4.setFieldValue("standard", selectedStandard.id);
        fetchControlsDataByStandard(formikStep2.values.standard);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikStep2.values.standard, selectedStandards]);

  useEffect(() => {
    if (controls.length > 0) {
      formikStep2.setFieldValue("slno", controls.length + 1);
    } else {
      formikStep2.setFieldValue("slno", 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls]);

  useEffect(() => {
    if (formikStep3.values.standard && selectedStandards) {
      const selectedStandard = selectedStandards.find(
        (standard) => standard.id === formikStep3.values.standard,
      );

      if (selectedStandard) {
        formikStep3.setFieldValue("standardName", selectedStandard.stdid);
        fetchControlsDataByStandard(formikStep3.values.standard);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikStep3.values.standard, selectedStandards]);

  useEffect(() => {
    if (formikStep3.values.standard && formikStep3.values.control) {
      fetchSubControlsDataByControl(
        formikStep3.values.standard,
        formikStep3.values.control,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikStep3.values.standard, formikStep3.values.control]);

  useEffect(() => {
    if (questions.length > 0) {
      formikStep3.setFieldValue("slno", questions.length + 1);
    } else {
      formikStep3.setFieldValue("slno", 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  const formContainerStyle = {
    marginBottom: "20px",
    display: "flex",
    justifyContent: "center",
  };

  const tableContainerStyle = {
    marginBottom: "20px",
  };

  const centerCardStyle = {
    textAlign: "center",
  };

  const dividerStyle = {
    margin: "20px 0",
  };

  const radioGroupStyle = {
    display: "flex",
    flexDirection: "row",
    gap: "10px", // Adjust the spacing between the radio buttons if needed
  };

  return (
    <div className="main-cont">
      <Card>
        <CardHeader
          title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">
                    <Breadcrumbs
                      paths={["CAP Configurator", "Configure Standard"]}
                    />
                  </label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    className="back font"
                    onClick={() => window.history.back()}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <CardContent>
          <FormControl component="fieldset">
            <FormLabel component="legend">Select Standard Type</FormLabel>
            <RadioGroup
              aria-label="standard-type"
              name="standardType"
              value={standardType}
              onChange={(e) => setStandardType(e.target.value)}
              style={radioGroupStyle}
              // disabled={activeStep !== 0}
            >
              {standardTypes.map((type) => (
                <FormControlLabel
                  key={type.id}
                  value={type.id}
                  control={<Radio />}
                  label={type.name}
                  disabled={activeStep !== 0}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {standardType && (
            <>
              <div className="d-flex justify-content-center">
                <ProgressBar
                  now={progress}
                  animated
                  label={`${progress}%`}
                  className="mt-2 mb-2 w-75"
                />
              </div>

              <CardContent style={centerCardStyle}>
                {activeStep === 0 && (
                  <div style={formContainerStyle}>
                    <Card className="row company-screen-bg col-md-10">
                      <CardContent>
                        <form onSubmit={formikStep1.handleSubmit}>
                          <FormControl fullWidth margin="normal">
                            <TextField
                              label="Name"
                              name="name"
                              // disabled={standardType === "standard-based"} // Disable the field if standardType is 'domain-based'
                              onChange={formikStep1.handleChange}
                              value={formikStep1.values.name}
                              error={
                                formikStep1.touched.name &&
                                Boolean(formikStep1.errors.name)
                              }
                              helperText={
                                formikStep1.touched.name &&
                                formikStep1.errors.name
                              }
                            />
                          </FormControl>
                          <FormControl fullWidth margin="normal">
                            <TextField
                              label="Description"
                              name="description"
                              // disabled={standardType === "standard-based"} // Disable the field if standardType is 'domain-based'
                              onChange={formikStep1.handleChange}
                              value={formikStep1.values.description}
                              error={
                                formikStep1.touched.description &&
                                Boolean(formikStep1.errors.description)
                              }
                              helperText={
                                formikStep1.touched.description &&
                                formikStep1.errors.description
                              }
                              multiline
                              rows={3} // Initially show 3 rows
                              maxRows={Infinity} // Allow unlimited rows with scroll after 3 lines
                            />
                          </FormControl>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Button
                              color="primary"
                              variant="contained"
                              type="submit"
                            >
                              Save & Next
                            </Button>
                            <Button
                              variant="contained"
                              className="danger mx-3"
                              onClick={() => {
                                formikStep1.resetForm();
                              }}
                            >
                              Clear
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                )}
                {activeStep === 0 &&
                  selectedStandards &&
                  selectedStandards.length > 0 && (
                    <div style={tableContainerStyle}>
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedStandards &&
                              selectedStandards.map((standard, index) => (
                                <TableRow key={index}>
                                  <TableCell>{standard.name}</TableCell>
                                  <TableCell
                                    style={{
                                      maxWidth: "300px",
                                      wordWrap: "break-word",
                                      whiteSpace: "normal",
                                    }}
                                  >
                                    {standard.description}
                                  </TableCell>
                                  <TableCell>
                                    {standard.status ? "Active" : "Inactive"}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={selectedStandards.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </div>
                  )}

                {activeStep === 1 && (
                  <div style={formContainerStyle}>
                    <Card className="row company-screen-bg col-md-10">
                      <CardContent>
                        <form onSubmit={formikStep2.handleSubmit}>
                          <div className="row ">
                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <InputLabel htmlFor="standard">
                                Standard
                              </InputLabel>
                              <Select
                                name="standard"
                                label="Standard"
                                value={formikStep2.values.standard}
                                onChange={formikStep2.handleChange}
                                error={
                                  formikStep2.touched.standard &&
                                  Boolean(formikStep2.errors.standard)
                                }
                                disabled
                              >
                                {selectedStandards &&
                                  selectedStandards.map((standard) => (
                                    <MenuItem
                                      key={standard.id}
                                      value={standard.id}
                                    >
                                      {standard.name}
                                    </MenuItem>
                                  ))}
                              </Select>
                              {formikStep2.touched.standard &&
                                formikStep2.errors.standard && (
                                  <div className="error-message">
                                    {formikStep2.errors.standard}
                                  </div>
                                )}
                            </FormControl>
                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <TextField
                                label="Control Id"
                                name="controlId"
                                disabled={standardType === "standard-based"} // Disable the field if standardType is 'domain-based'
                                onChange={formikStep2.handleChange}
                                value={formikStep2.values.controlId}
                                error={
                                  formikStep2.touched.controlId &&
                                  Boolean(formikStep2.errors.controlId)
                                }
                                helperText={
                                  formikStep2.touched.controlId &&
                                  formikStep2.errors.controlId
                                }
                              />
                            </FormControl>
                          </div>

                          <FormControl
                            className="col-md-6 px-2"
                            margin="normal"
                          >
                            <TextField
                              label="Control Name"
                              name="controlName"
                              disabled={standardType === "standard-based"}
                              onChange={formikStep2.handleChange}
                              value={formikStep2.values.controlName}
                              error={
                                formikStep2.touched.controlName &&
                                Boolean(formikStep2.errors.controlName)
                              }
                              helperText={
                                formikStep2.touched.controlName &&
                                formikStep2.errors.controlName
                              }
                            />
                          </FormControl>
                          <FormControl
                            className="col-md-6 px-2"
                            margin="normal"
                          >
                            <TextField
                              label="Control Description"
                              name="controlDescription"
                              disabled={standardType === "standard-based"}
                              onChange={formikStep2.handleChange}
                              value={formikStep2.values.controlDescription}
                              error={
                                formikStep2.touched.controlDescription &&
                                Boolean(formikStep2.errors.controlDescription)
                              }
                              helperText={
                                formikStep2.touched.controlDescription &&
                                formikStep2.errors.controlDescription
                              }
                              multiline
                              rows={1} // Initially show 3 rows
                              maxRows={Infinity} // Allow unlimited rows with scroll after 3 lines
                            />
                          </FormControl>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Button
                              color="primary"
                              variant="contained"
                              className="mx-2"
                              disabled={standardType === "standard-based"}
                              type="submit"
                            >
                              Save
                            </Button>
                            <Button
                              variant="contained"
                              className="danger mx-2"
                              disabled={standardType === "standard-based"}
                              onClick={() => {
                                let standardName =
                                  formikStep2.values.standardName;
                                let standard = formikStep2.values.standard;

                                formikStep2.resetForm();
                                formikStep2.setFieldValue(
                                  "standardName",
                                  standardName,
                                );
                                formikStep2.setFieldValue("standard", standard);
                                formikStep2.setFieldValue(
                                  "slno",
                                  controls.length > 0 ? controls.length + 1 : 1,
                                );
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              disabled={controls.length === 0}
                              onClick={() => {
                                setActiveStep((prev) => prev + 1);
                                setProgress(progress + 25);
                              }}
                            >
                              Next
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                )}
                {activeStep === 1 && controls.length > 0 && (
                  <div style={tableContainerStyle}>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            {/* <TableCell>Standard</TableCell>
                                                    <TableCell>Sl. No.</TableCell> */}
                            <TableCell>Control Id</TableCell>
                            <TableCell>Control Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {controls.map((control, index) => (
                            <TableRow key={index}>
                              {/* <TableCell>{control.Standard}</TableCell>
                                                        <TableCell>{control.slno}</TableCell> */}
                              <TableCell>{control.ControlID}</TableCell>
                              <TableCell>{control.Title}</TableCell>
                              <TableCell
                                style={{
                                  maxWidth: "300px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {control.Description}
                              </TableCell>
                              <TableCell>
                                {control.status ? "Active" : "Inactive"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      rowsPerPageOptions={[10, 25, 100]}
                      component="div"
                      count={controls.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </div>
                )}
                {activeStep === 2 && (
                  <div style={formContainerStyle}>
                    <Card className="row company-screen-bg col-md-10">
                      <CardContent>
                        <form onSubmit={formikStep3.handleSubmit}>
                          <div className="row ">
                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <InputLabel htmlFor="standard">
                                Standard
                              </InputLabel>
                              <Select
                                name="standard"
                                label="Standard"
                                value={formikStep3.values.standard}
                                onChange={formikStep3.handleChange}
                                error={
                                  formikStep3.touched.standard &&
                                  Boolean(formikStep3.errors.standard)
                                }
                                disabled
                              >
                                {selectedStandards &&
                                  selectedStandards.map((standard) => (
                                    <MenuItem
                                      key={standard.id}
                                      value={standard.id}
                                    >
                                      {standard.name}
                                    </MenuItem>
                                  ))}
                              </Select>
                              {formikStep3.touched.standard &&
                                formikStep3.errors.standard && (
                                  <div className="error-message">
                                    {formikStep3.errors.standard}
                                  </div>
                                )}
                            </FormControl>
                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <InputLabel htmlFor="control">Control</InputLabel>
                              <Select
                                name="control"
                                label="Control"
                                value={formikStep3.values.control}
                                onChange={formikStep3.handleChange}
                                error={
                                  formikStep3.touched.control &&
                                  Boolean(formikStep3.errors.control)
                                }
                                // disabled={standardType === 'standard-based'}
                              >
                                {controls.map((control) => (
                                  <MenuItem
                                    key={control.Control}
                                    value={control.ControlID}
                                  >
                                    {control.Title}
                                  </MenuItem>
                                ))}
                              </Select>
                              {formikStep3.touched.control &&
                                formikStep3.errors.control && (
                                  <div className="error-message">
                                    {formikStep3.errors.control}
                                  </div>
                                )}
                            </FormControl>
                          </div>
                          <div className="row ">
                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <TextField
                                label="SubControl ID"
                                name="subControlId"
                                disabled={standardType === "standard-based"}
                                onChange={formikStep3.handleChange}
                                value={formikStep3.values.subControlId}
                                error={
                                  formikStep3.touched.subControlId &&
                                  Boolean(formikStep3.errors.subControlId)
                                }
                                helperText={
                                  formikStep3.touched.subControlId &&
                                  formikStep3.errors.subControlId
                                }
                              />
                            </FormControl>

                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <TextField
                                label="Sub-Control"
                                name="subControlName"
                                disabled={standardType === "standard-based"}
                                onChange={formikStep3.handleChange}
                                value={formikStep3.values.subControlName}
                                error={
                                  formikStep3.touched.subControlName &&
                                  Boolean(formikStep3.errors.subControlName)
                                }
                                helperText={
                                  formikStep3.touched.subControlName &&
                                  formikStep3.errors.subControlName
                                }
                              />
                            </FormControl>
                          </div>
                          <div className="row ">
                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <TextField
                                label="Question Id"
                                name="questionId"
                                disabled={standardType === "standard-based"}
                                onChange={formikStep3.handleChange}
                                value={formikStep3.values.questionId}
                                error={
                                  formikStep3.touched.questionId &&
                                  Boolean(formikStep3.errors.questionId)
                                }
                                helperText={
                                  formikStep3.touched.questionId &&
                                  formikStep3.errors.questionId
                                }
                              />
                            </FormControl>

                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <TextField
                                label="Question"
                                name="questionName"
                                disabled={standardType === "standard-based"}
                                onChange={formikStep3.handleChange}
                                value={formikStep3.values.questionName}
                                error={
                                  formikStep3.touched.questionName &&
                                  Boolean(formikStep3.errors.questionName)
                                }
                                helperText={
                                  formikStep3.touched.questionName &&
                                  formikStep3.errors.questionName
                                }
                              />
                            </FormControl>
                          </div>
                          <FormControl fullWidth margin="normal">
                            <TextField
                              label="Description"
                              name="questionDescription"
                              disabled={standardType === "standard-based"}
                              onChange={formikStep3.handleChange}
                              value={formikStep3.values.questionDescription}
                              error={
                                formikStep3.touched.questionDescription &&
                                Boolean(formikStep3.errors.questionDescription)
                              }
                              helperText={
                                formikStep3.touched.questionDescription &&
                                formikStep3.errors.questionDescription
                              }
                              multiline
                              rows={1} // Initially show 3 rows
                              maxRows={Infinity} // Allow unlimited rows with scroll after 3 lines
                            />
                          </FormControl>

                          {/* US-CAP-103 + US-CAP-206: Question Type + Answer Config + Informational */}
                          {standardType !== "standard-based" && (
                            <div className="mt-2">
                              {/* Question Type selector */}
                              <div className="row">
                                <FormControl
                                  className="col-md-6 px-2"
                                  margin="normal"
                                >
                                  <InputLabel htmlFor="question_type">
                                    Question Type
                                  </InputLabel>
                                  <Select
                                    name="question_type"
                                    label="Question Type"
                                    value={formikStep3.values.question_type}
                                    onChange={(e) => {
                                      const newType = e.target.value;
                                      formikStep3.setFieldValue(
                                        "question_type",
                                        newType,
                                      );
                                      formikStep3.setFieldValue(
                                        "answer_config",
                                        getDefaultAnswerConfig(newType),
                                      );
                                    }}
                                  >
                                    {VALID_QUESTION_TYPES.map((t) => (
                                      <MenuItem key={t} value={t}>
                                        {QUESTION_TYPE_LABELS[t]}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <div className="col-md-6 px-2 d-flex align-items-center">
                                  <Chip
                                    label={`Type: ${
                                      QUESTION_TYPE_LABELS[
                                        formikStep3.values.question_type
                                      ] || formikStep3.values.question_type
                                    }`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </div>
                              </div>

                              {/* Answer Config Panel */}
                              {formikStep3.values.question_type !==
                                "five_option" && (
                                <div
                                  className="row mt-2 p-2"
                                  style={{
                                    background: "#f5f7fa",
                                    borderRadius: 6,
                                    margin: "8px 0",
                                  }}
                                >
                                  <div className="col-md-12 px-2">
                                    <strong style={{ fontSize: 13 }}>
                                      Answer Configuration
                                    </strong>
                                  </div>

                                  {/* yes_no labels */}
                                  {formikStep3.values.question_type ===
                                    "yes_no" && (
                                    <div className="row">
                                      {["yes", "no", "na"].map((key) => (
                                        <FormControl
                                          key={key}
                                          className="col-md-4 px-2"
                                          margin="dense"
                                        >
                                          <TextField
                                            label={`Label for "${key.toUpperCase()}"`}
                                            size="small"
                                            value={
                                              formikStep3.values.answer_config
                                                ?.labels?.[key] || ""
                                            }
                                            onChange={(e) =>
                                              formikStep3.setFieldValue(
                                                "answer_config",
                                                {
                                                  ...formikStep3.values
                                                    .answer_config,
                                                  labels: {
                                                    ...formikStep3.values
                                                      .answer_config?.labels,
                                                    [key]: e.target.value,
                                                  },
                                                },
                                              )
                                            }
                                          />
                                        </FormControl>
                                      ))}
                                    </div>
                                  )}

                                  {/* checkbox + radio: dynamic options list */}
                                  {(formikStep3.values.question_type ===
                                    "checkbox" ||
                                    formikStep3.values.question_type ===
                                      "radio") && (
                                    <div className="col-md-12">
                                      <div
                                        style={{
                                          fontSize: 12,
                                          marginBottom: 4,
                                          color: "#555",
                                        }}
                                      >
                                        Options (min 2)
                                      </div>
                                      {(
                                        formikStep3.values.answer_config
                                          ?.options || []
                                      ).map((opt, idx) => (
                                        <div
                                          key={idx}
                                          className="d-flex align-items-center mb-1"
                                        >
                                          <TextField
                                            size="small"
                                            value={opt}
                                            onChange={(e) => {
                                              const opts = [
                                                ...(formikStep3.values
                                                  .answer_config?.options ||
                                                  []),
                                              ];
                                              opts[idx] = e.target.value;
                                              formikStep3.setFieldValue(
                                                "answer_config",
                                                {
                                                  ...formikStep3.values
                                                    .answer_config,
                                                  options: opts,
                                                },
                                              );
                                            }}
                                            style={{ flex: 1, marginRight: 4 }}
                                          />
                                          <Tooltip title="Remove option">
                                            <IconButton
                                              size="small"
                                              disabled={
                                                (
                                                  formikStep3.values
                                                    .answer_config?.options ||
                                                  []
                                                ).length <= 2
                                              }
                                              onClick={() => {
                                                const opts = (
                                                  formikStep3.values
                                                    .answer_config?.options ||
                                                  []
                                                ).filter((_, i) => i !== idx);
                                                formikStep3.setFieldValue(
                                                  "answer_config",
                                                  {
                                                    ...formikStep3.values
                                                      .answer_config,
                                                    options: opts,
                                                    ...(formikStep3.values
                                                      .question_type ===
                                                      "checkbox" && {
                                                      maxSelect: Math.min(
                                                        formikStep3.values
                                                          .answer_config
                                                          ?.maxSelect ||
                                                          opts.length,
                                                        opts.length,
                                                      ),
                                                    }),
                                                  },
                                                );
                                              }}
                                            >
                                              <RemoveCircleOutlineIcon
                                                fontSize="small"
                                                color="error"
                                              />
                                            </IconButton>
                                          </Tooltip>
                                        </div>
                                      ))}
                                      <Button
                                        size="small"
                                        startIcon={<AddCircleOutlineIcon />}
                                        onClick={() =>
                                          formikStep3.setFieldValue(
                                            "answer_config",
                                            {
                                              ...formikStep3.values
                                                .answer_config,
                                              options: [
                                                ...(formikStep3.values
                                                  .answer_config?.options ||
                                                  []),
                                                "",
                                              ],
                                            },
                                          )
                                        }
                                      >
                                        Add Option
                                      </Button>
                                      {/* checkbox only: minSelect / maxSelect */}
                                      {formikStep3.values.question_type ===
                                        "checkbox" && (
                                        <div className="row mt-1">
                                          <FormControl
                                            className="col-md-6 px-2"
                                            margin="dense"
                                          >
                                            <TextField
                                              label="Min Selections"
                                              size="small"
                                              type="number"
                                              value={
                                                formikStep3.values.answer_config
                                                  ?.minSelect || 1
                                              }
                                              inputProps={{
                                                min: 1,
                                                max:
                                                  formikStep3.values
                                                    .answer_config?.options
                                                    ?.length || 2,
                                              }}
                                              onChange={(e) =>
                                                formikStep3.setFieldValue(
                                                  "answer_config",
                                                  {
                                                    ...formikStep3.values
                                                      .answer_config,
                                                    minSelect:
                                                      parseInt(
                                                        e.target.value,
                                                      ) || 1,
                                                  },
                                                )
                                              }
                                            />
                                          </FormControl>
                                          <FormControl
                                            className="col-md-6 px-2"
                                            margin="dense"
                                          >
                                            <TextField
                                              label="Max Selections"
                                              size="small"
                                              type="number"
                                              value={
                                                formikStep3.values.answer_config
                                                  ?.maxSelect || 2
                                              }
                                              inputProps={{
                                                min: 1,
                                                max:
                                                  formikStep3.values
                                                    .answer_config?.options
                                                    ?.length || 2,
                                              }}
                                              onChange={(e) =>
                                                formikStep3.setFieldValue(
                                                  "answer_config",
                                                  {
                                                    ...formikStep3.values
                                                      .answer_config,
                                                    maxSelect:
                                                      parseInt(
                                                        e.target.value,
                                                      ) || 2,
                                                  },
                                                )
                                              }
                                            />
                                          </FormControl>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* text_box config */}
                                  {formikStep3.values.question_type ===
                                    "text_box" && (
                                    <div className="col-md-12">
                                      <FormControl fullWidth margin="dense">
                                        <TextField
                                          label="Placeholder text"
                                          size="small"
                                          value={
                                            formikStep3.values.answer_config
                                              ?.placeholder || ""
                                          }
                                          onChange={(e) =>
                                            formikStep3.setFieldValue(
                                              "answer_config",
                                              {
                                                ...formikStep3.values
                                                  .answer_config,
                                                placeholder: e.target.value,
                                              },
                                            )
                                          }
                                        />
                                      </FormControl>
                                      <FormControl fullWidth margin="dense">
                                        <TextField
                                          label="Reviewer instructions"
                                          size="small"
                                          multiline
                                          rows={2}
                                          value={
                                            formikStep3.values.answer_config
                                              ?.reviewerInstructions || ""
                                          }
                                          onChange={(e) =>
                                            formikStep3.setFieldValue(
                                              "answer_config",
                                              {
                                                ...formikStep3.values
                                                  .answer_config,
                                                reviewerInstructions:
                                                  e.target.value,
                                              },
                                            )
                                          }
                                        />
                                      </FormControl>
                                      <FormControl
                                        className="col-md-4"
                                        margin="dense"
                                      >
                                        <TextField
                                          label="Max length (chars)"
                                          size="small"
                                          type="number"
                                          value={
                                            formikStep3.values.answer_config
                                              ?.maxLength || 2000
                                          }
                                          inputProps={{ min: 100, max: 10000 }}
                                          onChange={(e) =>
                                            formikStep3.setFieldValue(
                                              "answer_config",
                                              {
                                                ...formikStep3.values
                                                  .answer_config,
                                                maxLength:
                                                  parseInt(e.target.value) ||
                                                  2000,
                                              },
                                            )
                                          }
                                        />
                                      </FormControl>
                                    </div>
                                  )}

                                  {/* US-CAP-206: Informational toggle */}
                                  <div
                                    className="col-md-12 mt-2 pt-2"
                                    style={{ borderTop: "1px solid #ddd" }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={
                                            formikStep3.values.informational ===
                                            true
                                          }
                                          onChange={(e) =>
                                            formikStep3.setFieldValue(
                                              "informational",
                                              e.target.checked,
                                            )
                                          }
                                          size="small"
                                          sx={{
                                            color: "#1565C0",
                                            "&.Mui-checked": {
                                              color: "#1565C0",
                                            },
                                          }}
                                        />
                                      }
                                      label={
                                        <span style={{ fontSize: 13 }}>
                                          <strong>Informational only</strong>
                                          <span
                                            style={{
                                              color: "#666",
                                              marginLeft: 6,
                                            }}
                                          >
                                            — answer required but never counted
                                            in score
                                          </span>
                                        </span>
                                      }
                                    />
                                    {formikStep3.values.informational && (
                                      <div
                                        style={{
                                          padding: "5px 10px",
                                          background: "#EAF4FB",
                                          border: "1px solid #B3D9F5",
                                          borderRadius: 4,
                                          fontSize: 12,
                                          color: "#1565C0",
                                          marginTop: 4,
                                        }}
                                      >
                                        ℹ️ Users will see this question marked
                                        as informational. Their answer is
                                        recorded but excluded from the
                                        compliance score.
                                      </div>
                                    )}
                                  </div>

                                  {/* Live preview */}
                                  <div
                                    className="col-md-12 mt-2 pt-2"
                                    style={{ borderTop: "1px solid #ddd" }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: "#888",
                                        marginBottom: 6,
                                      }}
                                    >
                                      Live Preview
                                    </div>
                                    <QuestionRenderer
                                      question_type={
                                        formikStep3.values.question_type
                                      }
                                      answer_config={
                                        formikStep3.values.answer_config
                                      }
                                      informational={
                                        formikStep3.values.informational
                                      }
                                      readOnly={true}
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Informational toggle for five_option too */}
                              {formikStep3.values.question_type ===
                                "five_option" && (
                                <div className="col-md-12 mt-2">
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={
                                          formikStep3.values.informational ===
                                          true
                                        }
                                        onChange={(e) =>
                                          formikStep3.setFieldValue(
                                            "informational",
                                            e.target.checked,
                                          )
                                        }
                                        size="small"
                                        sx={{
                                          color: "#1565C0",
                                          "&.Mui-checked": { color: "#1565C0" },
                                        }}
                                      />
                                    }
                                    label={
                                      <span style={{ fontSize: 13 }}>
                                        <strong>Informational only</strong>
                                        <span
                                          style={{
                                            color: "#666",
                                            marginLeft: 6,
                                          }}
                                        >
                                          — answer required but never counted in
                                          score
                                        </span>
                                      </span>
                                    }
                                  />
                                  {formikStep3.values.informational && (
                                    <div
                                      style={{
                                        padding: "5px 10px",
                                        background: "#EAF4FB",
                                        border: "1px solid #B3D9F5",
                                        borderRadius: 4,
                                        fontSize: 12,
                                        color: "#1565C0",
                                        marginTop: 4,
                                      }}
                                    >
                                      ℹ️ Users will see this question marked as
                                      informational. Their answer is recorded
                                      but excluded from the compliance score.
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Button
                              color="primary"
                              variant="contained"
                              className="mx-3"
                              disabled={standardType === "standard-based"}
                              type="submit"
                            >
                              Save
                            </Button>
                            <Button
                              variant="contained"
                              className="danger"
                              disabled={standardType === "standard-based"}
                              onClick={() => {
                                let standard = formikStep3.values.standard;
                                let standardName =
                                  formikStep3.values.standardName;
                                let control = formikStep3.values.control;

                                formikStep3.resetForm();
                                formikStep3.setFieldValue(
                                  "standardName",
                                  standardName,
                                );
                                formikStep3.setFieldValue("standard", standard);
                                formikStep3.setFieldValue("control", control);
                                formikStep3.setFieldValue(
                                  "slno",
                                  questions.length > 0
                                    ? questions.length + 1
                                    : 1,
                                );
                                formikStep3.setFieldValue(
                                  "question_type",
                                  "five_option",
                                );
                                formikStep3.setFieldValue(
                                  "answer_config",
                                  null,
                                );
                                formikStep3.setFieldValue(
                                  "informational",
                                  false,
                                );
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              disabled={questions.length === 0}
                              onClick={() => {
                                setActiveStep((prev) => prev + 1);
                                setProgress(progress + 25);
                              }}
                            >
                              Next
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                )}
                {activeStep === 2 && questions.length > 0 && (
                  <div style={tableContainerStyle}>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>SubControlID</TableCell>
                            <TableCell>SubControlName</TableCell>
                            <TableCell>QuestionID</TableCell>
                            <TableCell>Question</TableCell>
                            {/* US-CAP-103 + US-CAP-206 */}
                            <TableCell>Type</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {questions.map((question, index) => (
                            <TableRow key={index}>
                              <TableCell>{question.SubControlID}</TableCell>
                              <TableCell
                                style={{
                                  maxWidth: "300px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {question.SubControlName}
                              </TableCell>
                              <TableCell>{question.Question_ID}</TableCell>
                              <TableCell
                                style={{
                                  maxWidth: "300px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {question.Question}
                              </TableCell>
                              {/* US-CAP-103 + US-CAP-206: type badge + informational indicator */}
                              <TableCell>
                                <Chip
                                  label={
                                    QUESTION_TYPE_LABELS[
                                      question.question_type
                                    ] || "Five Option"
                                  }
                                  size="small"
                                  color={
                                    question.question_type &&
                                    question.question_type !== "five_option"
                                      ? "secondary"
                                      : "default"
                                  }
                                  variant="outlined"
                                  sx={{
                                    mb: question.informational ? 0.5 : 0,
                                    display: "block",
                                    width: "fit-content",
                                  }}
                                />
                                {question.informational && (
                                  <Chip
                                    label="ℹ Info only"
                                    size="small"
                                    sx={{
                                      backgroundColor: "#EAF4FB",
                                      color: "#1565C0",
                                      fontSize: "10px",
                                      height: 18,
                                      border: "1px solid #B3D9F5",
                                    }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {question.status ? "Active" : "Inactive"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 100]}
                      component="div"
                      count={questions.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </div>
                )}
                {activeStep === 3 && (
                  <div style={formContainerStyle}>
                    <form onSubmit={formikStep4.handleSubmit}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <FormControl fullWidth margin="normal">
                            <InputLabel htmlFor="standard">Standard</InputLabel>
                            <Select
                              name="standard"
                              label="Standard"
                              value={formikStep4.values.standard}
                              disabled
                              error={
                                formikStep4.touched.standard &&
                                Boolean(formikStep4.errors.standard)
                              }
                            >
                              {selectedStandards &&
                                selectedStandards.map((standard) => (
                                  <MenuItem
                                    key={standard.id}
                                    value={standard.id}
                                  >
                                    {standard.name}
                                  </MenuItem>
                                ))}
                            </Select>
                            {formikStep4.touched.standard &&
                              formikStep4.errors.standard && (
                                <div className="error-message">
                                  {formikStep4.errors.standard}
                                </div>
                              )}
                          </FormControl>
                        </Grid>
                      </Grid>
                      {controls && controls.length > 0 && (
                        <>
                          {formikStep4.values.weightages.map(
                            (weightage, index) => (
                              <Box key={weightage.id} mb={2}>
                                {/* Group every two items into a single row */}
                                {index % 2 === 0 && (
                                  <Grid
                                    container
                                    spacing={2}
                                    alignItems="center"
                                  >
                                    {/* First question and weightage */}
                                    <Grid item xs={6}>
                                      <Grid
                                        container
                                        spacing={2}
                                        alignItems="center"
                                      >
                                        <Grid item xs={6}>
                                          <Typography
                                            variant="body1"
                                            color="textSecondary"
                                          >
                                            {
                                              formikStep4.values.weightages[
                                                index
                                              ]?.control
                                            }
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                          <TextField
                                            label="Weight"
                                            name={`weightages[${index}].weight`}
                                            disabled={
                                              standardType === "standard-based"
                                            }
                                            value={
                                              formikStep4.values.weightages[
                                                index
                                              ]?.weight
                                            }
                                            onChange={formikStep4.handleChange}
                                            error={
                                              formikStep4.touched.weightages?.[
                                                index
                                              ]?.weight &&
                                              Boolean(
                                                formikStep4.errors.weightages?.[
                                                  index
                                                ]?.weight,
                                              )
                                            }
                                            helperText={
                                              formikStep4.touched.weightages?.[
                                                index
                                              ]?.weight &&
                                              formikStep4.errors.weightages?.[
                                                index
                                              ]?.weight
                                            }
                                            fullWidth
                                            margin="normal"
                                            inputProps={{ step: "0.1" }}
                                          />
                                        </Grid>
                                        <Grid item xs={3}></Grid>
                                      </Grid>
                                    </Grid>

                                    {/* Second question and weightage (if it exists) */}
                                    {formikStep4.values.weightages[
                                      index + 1
                                    ] && (
                                      <Grid item xs={6}>
                                        <Grid
                                          container
                                          spacing={2}
                                          alignItems="center"
                                        >
                                          <Grid item xs={2}></Grid>
                                          <Grid item xs={6}>
                                            <Typography
                                              variant="body1"
                                              color="textSecondary"
                                            >
                                              {
                                                formikStep4.values.weightages[
                                                  index + 1
                                                ]?.control
                                              }
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={3}>
                                            <TextField
                                              label="Weight"
                                              name={`weightages[${
                                                index + 1
                                              }].weight`}
                                              disabled={
                                                standardType ===
                                                "standard-based"
                                              }
                                              value={
                                                formikStep4.values.weightages[
                                                  index + 1
                                                ]?.weight
                                              }
                                              onChange={
                                                formikStep4.handleChange
                                              }
                                              error={
                                                formikStep4.touched
                                                  .weightages?.[index + 1]
                                                  ?.weight &&
                                                Boolean(
                                                  formikStep4.errors
                                                    .weightages?.[index + 1]
                                                    ?.weight,
                                                )
                                              }
                                              helperText={
                                                formikStep4.touched
                                                  .weightages?.[index + 1]
                                                  ?.weight &&
                                                formikStep4.errors.weightages?.[
                                                  index + 1
                                                ]?.weight
                                              }
                                              fullWidth
                                              margin="normal"
                                              inputProps={{ step: "0.1" }}
                                            />
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                    )}
                                  </Grid>
                                )}
                              </Box>
                            ),
                          )}

                          <Box display="flex" justifyContent="center" mt={3}>
                            <Button
                              color="primary"
                              variant="contained"
                              disabled={standardType === "standard-based"}
                              type="submit"
                            >
                              Submit
                            </Button>
                            &emsp;
                            <Button
                              variant="contained"
                              onClick={() => {
                                setActiveStep((prev) => prev + 1);
                                setProgress(progress + 25);
                              }}
                            >
                              Next
                            </Button>
                          </Box>
                        </>
                      )}
                    </form>
                  </div>
                )}
                {activeStep === 4 && (
                  <div>
                    <h3 className="m-0 font-weight-bold text-primary">
                      {" "}
                      {selectedStandard.name} Review{" "}
                    </h3>

                    <div>
                      <div className=" shadow mb-1 my-2">
                        {selectedStandard.controls &&
                        selectedStandard.controls.length > 0 ? (
                          selectedStandard.controls.map((control, index) => (
                            <div
                              key={control._id}
                              className="card shadow mb-1 my-2 text-start"
                            >
                              <Accordion className="mx-1 my-2 custom-accordion-button">
                                <Accordion.Button
                                  as={Nav.Link}
                                  eventKey={`control-${index}`}
                                  onClick={() => toggleCollapse(index + 1)}
                                >
                                  <span className="m-0 font-weight-bold text-primary">
                                    {control.Title}
                                  </span>
                                  <FontAwesomeIcon
                                    icon={
                                      collapsedControls.includes(index + 1)
                                        ? faCaretDown
                                        : faCaretUp
                                    }
                                    className="float-right"
                                  />
                                </Accordion.Button>
                                <Accordion.Body eventKey={`control-${index}`}>
                                  <div className="card-body">
                                    <div className="d-flex justify-content-left">
                                      <p>
                                        <strong>Control ID:</strong>{" "}
                                        {control.ControlID}
                                      </p>{" "}
                                      &emsp; &emsp;
                                      <p>
                                        <strong>Weightage:</strong>{" "}
                                        {control.Weightage}
                                      </p>{" "}
                                      &emsp; &emsp;
                                      <p>
                                        <strong>Status:</strong>{" "}
                                        {control.status ? "Active" : "Inactive"}
                                      </p>{" "}
                                      &emsp; &emsp;
                                      {!control.Description && (
                                        <p>
                                          <strong>Description:</strong> No
                                          Description
                                        </p>
                                      )}
                                    </div>
                                    {control.Description && (
                                      <div>
                                        <p>
                                          <strong>Description:</strong>{" "}
                                          {control.Description ||
                                            "No Description"}
                                        </p>
                                      </div>
                                    )}

                                    {control.subControls &&
                                    control.subControls.length > 0 ? (
                                      <div className="subcontrols-section">
                                        <h6 className="m-0 font-weight-bold text-primary">
                                          SubControls:
                                        </h6>
                                        <ul style={{ listStyleType: "none" }}>
                                          {control.subControls.map(
                                            (subControl) => (
                                              <li key={subControl.id}>
                                                <Divider
                                                  style={dividerStyle}
                                                  className="my-2"
                                                />
                                                {subControl.SubControlID && (
                                                  <span>
                                                    <strong>
                                                      SubControl ID:
                                                    </strong>{" "}
                                                    {subControl.SubControlID}
                                                  </span>
                                                )}
                                                {subControl.SubControlName && (
                                                  <p>
                                                    <strong>
                                                      SubControl Name:
                                                    </strong>{" "}
                                                    {subControl.SubControlName}
                                                  </p>
                                                )}
                                                {subControl.Question_ID && (
                                                  <span>
                                                    <strong>
                                                      Question ID:
                                                    </strong>{" "}
                                                    {subControl.Question_ID}
                                                  </span>
                                                )}
                                                {subControl.Question && (
                                                  <p>
                                                    <strong>Question:</strong>{" "}
                                                    {subControl.Question}
                                                  </p>
                                                )}
                                                {subControl.Description && (
                                                  <p>
                                                    <strong>
                                                      Description:
                                                    </strong>{" "}
                                                    {subControl.Description ||
                                                      "No Description"}
                                                  </p>
                                                )}
                                                {/* US-CAP-103 + US-CAP-206: type + informational */}
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    gap: 6,
                                                    flexWrap: "wrap",
                                                    marginTop: 4,
                                                  }}
                                                >
                                                  <Chip
                                                    label={
                                                      QUESTION_TYPE_LABELS[
                                                        subControl.question_type
                                                      ] || "Five Option"
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    color={
                                                      subControl.question_type &&
                                                      subControl.question_type !==
                                                        "five_option"
                                                        ? "secondary"
                                                        : "default"
                                                    }
                                                  />
                                                  {subControl.informational && (
                                                    <Chip
                                                      label="ℹ Informational"
                                                      size="small"
                                                      sx={{
                                                        backgroundColor:
                                                          "#EAF4FB",
                                                        color: "#1565C0",
                                                        fontSize: "10px",
                                                        height: 20,
                                                        border:
                                                          "1px solid #B3D9F5",
                                                      }}
                                                    />
                                                  )}
                                                </div>
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      </div>
                                    ) : (
                                      <p>No subcontrols available.</p>
                                    )}
                                  </div>
                                </Accordion.Body>
                              </Accordion>
                            </div>
                          ))
                        ) : (
                          <div className="alert alert-warning mx-1">
                            No controls available for this standard.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex justify-content-center mt-4">
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={() => {
                          navigate("/cap/configurestandards");
                        }}
                        type="submit"
                      >
                        Complete
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
export default ConfigureNewStandard;
